import React, { useEffect, useRef } from 'react';
import { JourneyItinerary } from '@eurostar/ui-components';
import PropTypes from 'prop-types';
import {
  ANY_BELGIAN_STATION_UIC,
  ADS_THROUGH_BRUSSELS_UIC,
  ANY_BELGIAN_STATION_NAME,
  ADS_THROUGH_BRUSSELS_NAME,
  SBNS_SERVICE_TYPE
} from 'constants/booking';
import { legShape } from 'constants/propTypes';
import { brandCode } from 'constants/brandCodes';
import { isDBLeg } from 'utils/segmentTypes';
import { StyledTd } from 'components/ui/Table/Table.styles';
import { StyledJourneyDetailsContainer } from './SearchResultsTables.styles';

const ABS_ADS_COMMON_PROPS = {
  serviceType: 'SB',
  timing: {
    departureTime: '',
    arrivalTime: '',
    duration: 0
  }
};

const JourneyDetailsSection = ({ journey, colSpan, isVisible, isIndirect, language, origin, destination }) => {
  // Add additional data attributes to the relevant elements in the JourneyItinerary
  // the CSS overrides will take care of the rebranding styling
  const itineraryRef = useRef();
  useEffect(() => {
    if (!itineraryRef.current) {
      return;
    }

    const serviceIcons = itineraryRef.current.querySelectorAll('[data-testid="service-icon"]');
    for (const serviceIcon of serviceIcons) {
      const serviceType = serviceIcon.getAttribute('type');
      serviceIcon.parentElement.setAttribute('data-replace-service-icon', serviceType);
    }
  }, [itineraryRef, isVisible, isIndirect]);

  if (!isVisible || !isIndirect) {
    return null;
  }

  const legs = journey.fares[0].legs.map(({ origin, destination, serviceType, timing }) => ({
    originName: origin.name,
    destinationName: destination.name,
    serviceType: isDBLeg({ serviceType }) ? brandCode.DB : serviceType.brandCode || serviceType.code,
    timing: {
      departureTime: timing.departs,
      arrivalTime: timing.arrives,
      duration: timing.duration
    }
  }));

  const firstLeg = journey.fares[0].legs[0];

  if (origin === ANY_BELGIAN_STATION_UIC) {
    legs.unshift({
      originName: ANY_BELGIAN_STATION_NAME,
      destinationName: firstLeg.origin.name,
      additionalProduct: 'ABS',
      ...ABS_ADS_COMMON_PROPS
    });
  }

  if (destination === ANY_BELGIAN_STATION_UIC) {
    legs.push({
      originName: firstLeg.destination.name,
      destinationName: ANY_BELGIAN_STATION_NAME,
      additionalProduct: 'ABS',
      ...ABS_ADS_COMMON_PROPS
    });
  }

  if (origin === ADS_THROUGH_BRUSSELS_UIC) {
    legs.unshift({
      originName: ADS_THROUGH_BRUSSELS_NAME,
      destinationName: firstLeg.origin.name,
      additionalProduct: 'ADS',
      ...ABS_ADS_COMMON_PROPS,
      serviceType: SBNS_SERVICE_TYPE
    });
  }

  if (destination === ADS_THROUGH_BRUSSELS_UIC) {
    legs.push({
      originName: firstLeg.destination.name,
      destinationName: ADS_THROUGH_BRUSSELS_NAME,
      additionalProduct: 'ADS',
      ...ABS_ADS_COMMON_PROPS,
      serviceType: SBNS_SERVICE_TYPE
    });
  }

  return (
    <tr>
      <StyledTd colSpan={colSpan} key="journeyDetails">
        <StyledJourneyDetailsContainer ref={itineraryRef}>
          <JourneyItinerary legs={legs} testId="journey-itinerary" language={language} />
        </StyledJourneyDetailsContainer>
      </StyledTd>
    </tr>
  );
};

JourneyDetailsSection.propTypes = {
  isVisible: PropTypes.bool,
  isIndirect: PropTypes.bool,
  colSpan: PropTypes.number,
  language: PropTypes.string,
  journey: PropTypes.shape({
    fares: PropTypes.arrayOf(
      PropTypes.shape({
        legs: PropTypes.arrayOf(legShape)
      })
    )
  }),
  origin: PropTypes.string,
  destination: PropTypes.string
};

export default JourneyDetailsSection;
